export interface ParsedSection {
  position: number;
  title: string;
  content: string;
  wordCount: number;
  speakerNotes: string[];
}

export interface ParseResult {
  sections: ParsedSection[];
  totalWordCount: number;
  totalCharCount: number;
}

const SPEAKER_NOTE_RE = /^> .+$/;

export function stripSpeakerNotes(text: string): string {
  if (!text) return text;
  const lines = text.split('\n').filter((line) => !SPEAKER_NOTE_RE.test(line));
  return lines.join('\n');
}

export function extractSpeakerNotes(text: string): string[] {
  if (!text) return [];
  return text
    .split('\n')
    .filter((line) => SPEAKER_NOTE_RE.test(line))
    .map((line) => line.slice(2));
}

/**
 * Insert section break markers (---) into rawContent at boundaries detected by the LLM.
 * Speaker notes stay in their original positions; only --- markers change.
 */
export function insertSectionBreaks(rawContent: string, sectionContents: string[]): string {
  if (!rawContent || sectionContents.length <= 1) return rawContent;

  const stripped = stripSpeakerNotes(rawContent);

  // Find each section's start offset in the stripped text
  let searchFrom = 0;
  const sectionStarts: number[] = [];
  for (const content of sectionContents) {
    const trimmed = content.trim();
    const firstLine =
      trimmed
        .split('\n')
        .find((l) => l.trim())
        ?.trim() ?? trimmed;
    const idx = stripped.indexOf(firstLine, searchFrom);
    sectionStarts.push(idx >= 0 ? idx : searchFrom);
    if (idx >= 0) searchFrom = idx + trimmed.length;
  }

  // Walk original lines, inserting --- at section boundaries
  const lines = rawContent.split('\n');
  const result: string[] = [];
  let strippedPos = 0;
  let currentSection = 0;

  for (const line of lines) {
    if (SPEAKER_NOTE_RE.test(line)) {
      result.push(line);
    } else if (line.trim() === '---') {
      strippedPos += line.length + 1;
    } else {
      if (
        currentSection < sectionStarts.length - 1 &&
        strippedPos >= sectionStarts[currentSection + 1]
      ) {
        result.push('---');
        currentSection++;
      }
      result.push(line);
      strippedPos += line.length + 1;
    }
  }

  return result.join('\n');
}

export function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

export function parseSections(rawContent: string): ParseResult {
  if (!rawContent || !rawContent.trim()) {
    return { sections: [], totalWordCount: 0, totalCharCount: 0 };
  }

  const chunks = rawContent.split(/^---$/m);
  const sections: ParsedSection[] = [];
  let position = 0;

  for (const chunk of chunks) {
    const trimmed = chunk.trim();
    if (!trimmed) continue;

    const speakerNotes = extractSpeakerNotes(trimmed);
    const stripped = stripSpeakerNotes(trimmed).trim();

    sections.push({
      position,
      title: `Section ${position + 1}`,
      content: stripped,
      wordCount: countWords(stripped),
      speakerNotes,
    });

    position++;
  }

  const totalWordCount = sections.reduce((sum, s) => sum + s.wordCount, 0);
  const totalCharCount = rawContent.length;

  return { sections, totalWordCount, totalCharCount };
}
